<template>
	<!-- 添加loading遮罩层 -->
	<div v-loading="isLoading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
		<div class="about">
			<carousel v-if="configData?.coverview_banner?.length > 0" :imgList="configData?.coverview_banner"></carousel>
			<div class="about_tabs">
				<!-- 绑定当前路由对应的tab名称 -->
				<el-tabs v-model="activeTab" @tab-click="handleTabClick">
					<el-tab-pane label="企业概况" name="company">
						<div class="about_title">企业概况</div>
						<div class="about_title_en">COMPANY OVERVIEW</div>
						<div class="about_content" v-if="configData?.coverview_desc.length > 0">
							<RichTextWithImages
								:content="configData?.coverview_desc[0]"
								:enable-image-preview="true"
								:max-image-width="'100%'"
							/>
						</div>
						<!-- 暂无数据 -->
						<div v-if="configData?.coverview_desc?.length === 0 && isLoading === false" class="empty_box bm bm-ver bm-ac bm-pc">
							<img src="@/assets/empty_icon.png" alt="">
							<div class="empty_text">
								暂无数据
							</div>
						</div>
					</el-tab-pane>
					<el-tab-pane label="发展历程" name="history">
						<div class="about_title">发展历程</div>
						<div class="about_title_en">DEVELOPMENT HISTORY</div>
						<div v-if="timelineData.length > 0" class="about_content_history">
							<div class="timeline-container" :style="{ height: timelineData.length * 90 + 'px' }">
								<!-- 主轴线 - 只在节点之间显示 -->
								<div 
									v-if="timelineData.length > 1"
									class="timeline-connectors" 
									:style="{ 
										height: `${(timelineData.length - 1) * 90}px`,
										top: '45px'
									}"
								></div>
	
								<!-- 事件节点列表 -->
								<div
									v-for="(event, index) in timelineData"
									:key="index"
									class="timeline-item"
									:style="{ top: `${index * 90}px` }"
									@mouseenter="hoverIndex = index"
									@mouseleave="hoverIndex = -1"
								>
									<!-- 事件节点圆点 - 根据悬停状态切换样式 -->
									<div
										class="timeline-node"
										:class="{ 'timeline-node-active': hoverIndex === index }"
									></div>
	
									<!-- 左侧内容区域（偶数项：索引1,3...） -->
									<div v-if="index % 2 === 1" class="content-left">
										<!-- 圆形图标 -->
										<div v-if="event.image" class="icon-circle">
											<img class="circle_img" :src="event.image" alt="" />
										</div>
										<!-- 文字内容 -->
										<div class="text-content">
											<el-tooltip :content="event.title" placement="top" :disabled="!isTitleOverflow(titleRefs[index])">
												<div 
													class="title" 
													:ref="(el) => { titleRefs[index] = el }"
												>
													{{ event.title }}
												</div>
											</el-tooltip>
											<el-tooltip :content="event.description" placement="top" :disabled="!isDescriptionOverflow(descriptionRefs[index])">
												<div 
													class="description" 
													:ref="(el) => { descriptionRefs[index] = el }"
												>
													{{ event.description }}
												</div>
											</el-tooltip>
										</div>
									</div>
	
									<!-- 右侧内容区域（奇数项：索引0,2,4...） -->
									<div v-else class="content-right">
										<!-- 文字内容 -->
										<div class="text-content">
											<el-tooltip :content="event.title" placement="top" :disabled="!isTitleOverflow(titleRefs[index])">
												<div 
													class="title" 
													:ref="(el) => { titleRefs[index] = el }"
												>
													{{ event.title }}
												</div>
											</el-tooltip>
											<el-tooltip :content="event.description" placement="top" :disabled="!isDescriptionOverflow(descriptionRefs[index])">
												<div 
													class="description" 
													:ref="(el) => { descriptionRefs[index] = el }"
												>
													{{ event.description }}
												</div>
											</el-tooltip>
										</div>
	
										<!-- 圆形图标 -->
										<div v-if="event.image" class="icon-circle">
											<img class="circle_img" :src="event.image" alt="" />
										</div>
									</div>
								</div>
							</div>
						</div>
						<!-- 暂无数据 -->
						<div v-if="timelineData?.length === 0 && isLoading === false" class="empty_box bm bm-ver bm-ac bm-pc">
							<img src="@/assets/empty_icon.png" alt="">
							<div class="empty_text">
								暂无数据
							</div>
						</div>
					</el-tab-pane>
				</el-tabs>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted, watch, onBeforeMount, nextTick } from 'vue'
	import carousel from '@/components/carousel.vue'
	import RichTextWithImages from '@/components/RichTextWithImages.vue'
	import { checkIfEllipsisActive } from '@/utils/overflowUtil.ts'

	const route = useRoute()
	const router = useRouter()

	// 添加refs用于检测文本溢出
	const titleRefs = ref([])
	const descriptionRefs = ref([])

	// 检测标题是否溢出（单行）
	const isTitleOverflow = (element) => {
		if (element) {
			return checkIfEllipsisActive(element, 1)
		}
		return false
	}

	// 检测描述是否溢出（两行）
	const isDescriptionOverflow = (element) => {
		if (element) {
			return checkIfEllipsisActive(element, 2)
		}
		return false
	}

	// 根据当前路由计算activeTab
	const activeTab = computed({
		get() {
			// 从路由路径判断当前是哪个tab
			const path = route.path;
			if (path.includes('/about/history')) {
				return 'history';
			}
			// 默认或/about/company都返回'company'
			return 'company';
		},
		set(value) {
			// 当tab切换时，跳转到对应路由
			if (value === 'history') {
				router.push('/about/history');
			} else {
				router.push('/about/company');
			}
		}
	});

	// 添加悬停状态变量
	const hoverIndex = ref(-1);

	// 监听路由变化，确保activeTab正确更新
	watch(() => route.path, (newPath) => {
	console.log('路由变化:', newPath);
	}, { immediate: true });

	// tab点击处理函数
	const handleTabClick = (tab: any) => {
	const tabName = tab.props.name;
	// 直接通过activeTab的setter来处理路由跳转
	activeTab.value = tabName;
	}

	// 添加loading状态
	const isLoading = ref(false)

	import { useConfigStore } from "@/stores/index.ts";
	const store = useConfigStore()
	const configData = computed(() => {
		console.log(store.configData,"store.configData")
		return store.configData
	})


	import { getCompanyProfiles } from '@/apis'
	let timelineData = ref([])
	onBeforeMount(async () => {
		isLoading.value = true
		const res = await getCompanyProfiles()
		if(res && res.msgCode === "100") {
			timelineData.value = res.responseData
		}
		isLoading.value = false
	})

</script>

<style lang="styl" scoped>
	.about{
		width 100%
		min-height calc(100% - 70px -220px)
		background-color #fff
		min-width 1200px
		padding-bottom 100px
		:deep(.el-tabs__active-bar) {
			transition: none !important;
		}
		:deep(.el-tabs__nav-wrap){
			box-shadow: 0px 4px 14px 0px rgba(157,170,197,0.21);
			.el-tabs__nav{
				width 100%
				padding 0 140px
			}
		}
		:deep(.el-tabs__nav-wrap:after) {
			display: none;
		}
		.about_tabs{
			.about_title{
				font-weight: 500;
				font-size: 24px;
				color: #252525;
				line-height: 24px;
				text-align: center;
				margin-top: 73px;
			}
			.about_title_en{
				font-size: 12px;
				color: #C1C1C1;
				line-height: 24px;
				text-align: center;
			}
			.about_content{
				padding 20px 140px
				/* text-align: center; */
				min-height 300px
			}
			.about_content_history{
				padding: 50px 140px 0;
				width: 100%;
				.timeline-container {
					position: relative;
					width: 100%;
					background-color: #FFFFFF;
				}

				/* 节点间连接线 */
				.timeline-connectors {
					position: absolute;
					left: 50%;
					width: 2px;
					background-color: #E9E9E9;
					transform: translateX(-50%);
					z-index: 1;
				}

				/* 事件项 */
				.timeline-item {
					position: absolute;
					left: 0;
					width: 100%;
					height: 90px;
					display: flex;
					align-items: center;
				}

				/* 事件节点圆点 - 默认实心 */
				.timeline-node {
					position: absolute;
					left: 50%;
					transform: translate(-50%, -50%);
					width: 9px;
					height: 9px;
					background-color: #0055A2;
					border-radius: 50%;
					border: 2px solid #0055A2;
					z-index: 2;
					transition: all 0.3s ease;
				}

				/* 高亮节点 - 空心圆形 */
				.timeline-node-active {
					background-color: white;
					border: 3px solid #0055A2;
					width: 12px;
					height: 12px;
				}

				/* 左侧内容区域 */
				.content-left {
					position: absolute;
					right: 50%;
					width: 45%;
					padding-right: 60px;
					display: flex;
					justify-content: flex-end;
					align-items: center;
					gap: 15px;
					text-align: right;
				}

				/* 右侧内容区域 */
				.content-right {
					position: absolute;
					left: 50%;
					width: 45%;
					padding-left: 60px;
					display: flex;
					align-items: center;
					gap: 15px;
				}

				/* 圆形图标 */
				.icon-circle {
					width: 80px;
					height: 80px;
					min-width: 80px; /* 防止图标被压缩 */
					border: 2px solid #0055A2;
					border-radius: 50%;
					background-color: #F5F7FA;
					transition: all 0.3s ease;
				}

				/* 文字内容 */
				.text-content {
					/* width: 100%; */
					max-width: 100%;
					overflow: hidden;
				}
				
				.circle_img{
					width: 100%;
					height: 100%;
					object-fit: cover;
					object-position: center;
					border-radius: 50%;
				}

				/* 年份文字 */
				.title {
					font-size: 18px;
					font-weight: 600;
					color: #323232;
					line-height: 1.2;
					margin-bottom: 6px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					width: 100%;
				}

				/* 描述文字 */
				.description {
					font-weight: normal;
					font-size: 13px;
					color: #626262;
					line-height: 20px;
					display: -webkit-box;
					-webkit-line-clamp: 2;
					-webkit-box-orient: vertical;
					overflow: hidden;
					text-overflow: ellipsis;
					width: 100%;
					word-break: break-word;
					overflow-wrap: break-word;
				}
			}
		}
		.empty_box{
			width: 100%;
			height: 100%;
			min-height: 40vh;
		}
	}
</style>